/**
 * Dynamically updates the promo announcement bar based on cart total.
 * Free shipping threshold: $75 USD / $95 CAD.
 * Intercepts fetch calls to Shopify cart endpoints so it works with any cart
 * app (UPcart, theme cart drawer, etc.) without relying on theme-specific events.
 */

const isCanada = window.Shopify?.country === 'CA';
const suffix = isCanada ? ' CAD' : '';
const FREE_SHIPPING_THRESHOLD = isCanada ? 9500 : 7500;

/*
 * COMMENTED OUT: Multi-tier rewards
 * const TIERS = isCanada
 *   ? [
 *       { threshold: 9500, reward: 'FREE Shipping' },
 *       { threshold: 12500, reward: '15% OFF' },
 *       { threshold: 17000, reward: '20% OFF' },
 *     ]
 *   : [
 *       { threshold: 7500, reward: 'FREE Shipping' },
 *       { threshold: 10000, reward: '15% OFF' },
 *       { threshold: 13500, reward: '20% OFF' },
 *     ];
 */

const SUCCESS_MESSAGE = "You've unlocked <b>FREE SHIPPING</b>! \u{1F389}";

/**
 * @param {number} totalCents - Cart total in cents
 * @returns {{ html: string, dynamic: boolean }}
 */
function getPromoMessage(totalCents, originalHTML) {
  if (totalCents <= 0) return { html: originalHTML, dynamic: false };
  if (totalCents >= FREE_SHIPPING_THRESHOLD) return { html: SUCCESS_MESSAGE, dynamic: true };

  const awayDollars = Math.ceil((FREE_SHIPPING_THRESHOLD - totalCents) / 100);
  return { html: `You're <b>$${awayDollars}${suffix}</b> away from <b>FREE Shipping</b>!`, dynamic: true };
}

class PromoAnnouncement {
  /** @type {HTMLElement} */
  #el;
  /** @type {string} */
  #originalHTML;

  /**
   * @param {HTMLElement} el - The announcement text element
   */
  constructor(el) {
    this.#el = el;
    this.#el.style.transition = 'opacity 0.2s ease';
    this.#originalHTML = el.dataset.promoOriginal || el.innerHTML;

    const initialTotal = Number(el.dataset.cartTotal) || 0;
    this.#update(initialTotal, false);
  }

  async refresh() {
    try {
      const res = await fetch('/cart.json');
      const cart = await res.json();
      this.#update(cart.total_price, true);
    } catch {
      // Keep current text
    }
  }

  /**
   * @param {number} count - Number of items in cart
   * @param {boolean} animate - Whether to crossfade the transition
   */
  #update(count, animate) {
    const { html, dynamic } = getPromoMessage(count, this.#originalHTML);

    if (this.#el.innerHTML === html) return;

    const applyUpdate = () => {
      this.#el.innerHTML = html;
      this.#el.style.fontWeight = dynamic ? 'normal' : '';
    };

    if (!animate) {
      applyUpdate();
      return;
    }

    this.#el.style.opacity = '0';
    setTimeout(() => {
      applyUpdate();
      this.#el.style.opacity = '1';
    }, 200);
  }
}

// Initialize instances
/** @type {PromoAnnouncement[]} */
const instances = [];
document.querySelectorAll('[data-promo-bar]').forEach((el) => {
  instances.push(new PromoAnnouncement(el));
});

// Intercept fetch to detect cart changes from any source (UPcart, theme, etc.)
if (instances.length > 0) {
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const result = originalFetch.apply(this, arguments);
    const url = typeof input === 'string' ? input : input instanceof Request ? input.url : '';
    if (/\/cart\/(add|change|update|clear)/.test(url)) {
      result.then(() => {
        setTimeout(() => instances.forEach((i) => i.refresh()), 300);
      }).catch(() => {});
    }
    return result;
  };
}
